import React, { useEffect, useRef, useState, useMemo } from "react";

import {
  Chart,
  ChartAxis,
  ChartGroup,
  ChartLine,
  ChartThemeColor,
  ChartLegendTooltip,
  createContainer
} from "@patternfly/react-charts/victory";

import { getResizeObserver } from "@patternfly/react-core";

const LineChart = ({ isLoadedCharts, qualification = [] }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  /**
   * Theme state (reactive)
   */
  const [isDark, setIsDark] = useState(
    document.documentElement.classList.contains("pf-v6-theme-dark")
  );

  /**
   * Observe theme changes on <html>
   */
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const dark = document.documentElement.classList.contains("pf-v6-theme-dark");
      setIsDark(dark);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    });

    return () => observer.disconnect();
  }, []);

  /**
   * Colors (reactive now)
   */
  const textColor = isDark ? "#f5f5f5" : "#151515";
  const gridColor = isDark ? "#787878" : "#d2d2d2";

  /**
   * Legend data (memoized to update on theme change)
   */
  const legendData = useMemo(
    () =>
      qualification.map(item => ({
        childName: item.legendItem?.name?.toLowerCase(),
        name: item.legendItem?.name,
        labels: { fill: textColor }
      })),
    [qualification, textColor]
  );

  const CursorVoronoiContainer = useMemo(
    () => createContainer("voronoi", "cursor"),
    []
  );

    /**
   * Responsive resize
   */
  useEffect(() => {
    if (!isLoadedCharts) {
      return;
    }

    const element = containerRef.current;

    if (!element) {
      return;
    }

    let active = true;

    const updateWidth = () => {
      if (
        !active ||
        !element.isConnected ||
        containerRef.current !== element
      ) {
        return;
      }

      setWidth(element.clientWidth);
    };

    const disconnectObserver = getResizeObserver(
      element,
      updateWidth
    );

    updateWidth();

    return () => {
      active = false;

      if (typeof disconnectObserver === "function") {
        disconnectObserver();
      }
    };
  }, [isLoadedCharts]);

  if (!isLoadedCharts) return null;

  return (
    <div ref={containerRef}>
      <Chart
        width={width}
        height={275}
        themeColor={ChartThemeColor.multiOrdered}

        legendData={legendData}
        legendPosition="bottom"
        legendOrientation="horizontal"
        legendAllowWrap

        legendStyle={{
          labels: {
            fill: textColor,
            fontSize: 12
          }
        }}

        maxDomain={{ y: 100 }}
        minDomain={{ y: 0 }}

        padding={{
          top: 50,
          bottom: 75,
          left: 50,
          right: 50
        }}

        containerComponent={
          <CursorVoronoiContainer
            mouseFollowTooltips
            voronoiDimension="x"
            cursorDimension="x"
            voronoiPadding={50}
            labels={({ datum }) => `${datum.y}`}
            labelComponent={
              <ChartLegendTooltip
                legendData={legendData}
                title={d => d.x}
              />
            }
          />
        }
      >
        <ChartAxis
          fixAxisLabelHeight
          fixLabelOverlap
          style={{
            tickLabels: {
              fill: textColor,
              fontSize: 12
            },
            axis: {
              stroke: gridColor
            }
          }}
        />

        <ChartAxis
          dependentAxis
          showGrid
          tickValues={[20, 40, 60, 80, 100]}
          style={{
            tickLabels: {
              fill: textColor,
              fontSize: 12
            },
            grid: {
              stroke: gridColor
            }
          }}
        />

        <ChartGroup>
          {qualification.map((item, idx) => (
            <ChartLine
              key={idx}
              data={item.datapoints || []}
              name={item.legendItem?.name?.toLowerCase()}
            />
          ))}
        </ChartGroup>
      </Chart>
    </div>
  );
};

export default LineChart;
